import * as React from "react";
import * as ReactDOM from "react-dom";
import { getCalendars } from "../../../api/calendars";
import { getGridChildStyle } from "../../../utils/utils";
import { IColor } from "../../../models";
import { ICalendar } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ToolBar, ToolBarItem, ToolBarSeparator } from "../../../components/toolbar";
import { InputSize } from "../../../components/input";
import { TextBox } from "../../../components/textBox";
import { Button, ButtonType, ButtonSize } from "../../../components/button";
import { Table, ColorColumn, ITableColumn, ITableRowEventArgs } from "../../../components/table";
import SettingsNav from "../nav";
import Dialog from "./dialog";
import { TabControl, Tab, TabControlTemplate } from "../../../components/tabControl";
import { CALENDAR_COLORS } from "../../../utils/constants";

interface IProperties
{
}

interface IState
{
    /**
     * Gibt an, ob gerade geladen wird.
     */
    isLoading?: boolean,

    /**
     * Das momentan selektierte TabItem.
     */
    selectedTabIndex?: number,

    /**
     * Gibt an, ob der Dialog geöffnet ist.
     */
    isDialogOpen?: boolean,

    /**
     * Gibt an, ob der Dialog zum bearbeiten eines Datensatzes geöffnet wird.
     */
    isDialogInEditMode?: boolean,

    /**
     * Enthält Text der zum Filtern der Liste verwendet wird.
     */
    filterValue?: string,

    /**
     * Enthält die geladenen Daten.
     */
    calendars?: ICalendar[],

    /**
     * Enthält die gefilterten Daten.
     */
    filteredCalendars?: ICalendar[],

    /**
     * Enthält den momentan selektierten Datensatz.
     */
    selectedItem?: ICalendar,

    /**
     * Enthält den momentan selektierten Zeilenindex.
     */
    selectedRowIndex?: string
}

export default class Calendars extends React.Component<IProperties, IState>
{
    /**
     * Erstellt eine neue Instanz der Klasse @see Calendars.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: IProperties)
    {
        super(props);

        this.state = {
            isLoading: true
        }

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        var calendars = await getCalendars();

        this.setState({
            isLoading: false,
            calendars: calendars,
            filteredCalendars: calendars
        });
    }

    /**
     * Aktualisiert die Liste.
     */
    private async refreshAsync()
    {
        this.setState({
            isLoading: true,
            filterValue: null
        });

        await this.initAsync(this.props);
    }

    /**
     * Wird ausgeführt, sobald sich der Text der Filter TextBox ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     */
    private onFilterTextChanged = (value: any[], originalValue: string[]): void =>
    {
        const { calendars } = this.state;
        var filter: string = value[0];
        var filteredData: ICalendar[] = [];

        if (filter != null && filter !== "")
            filteredData = calendars.where(m => m.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0);
        else
            filteredData = calendars;

        this.setState({
            filterValue: value[0],
            filteredCalendars: filteredData
        });
    }

    /**
     * Wird ausgeführt sobald auf eine Zeile geklickt wird. Die Daten der ausgewählten
     * Zeile werden hier für weitere Zwecke zwischengespeichert.
     * @param e
     */
    private onRowClick = (e: ITableRowEventArgs<any>) =>
    {
        this.setState({
            selectedItem: e.dataSource,
            selectedRowIndex: e.index
        });
    }

    /**
     * Öffnet den Dialog zum erstellen eines neuen Datensatzes.
     */
    private onNewClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: false
        });
    }

    /**
     * Öffnet den Dialog zum bearbeiten eines Datensatzes.
     */
    private onEditClick = () =>
    {
        this.setState({
            isDialogOpen: true,
            isDialogInEditMode: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     * @param refresh Angabe, ob die Ansicht aktualisiert werden soll.
     */
    private onDialogClosed = async (refresh: boolean) =>
    {
        if (refresh)
        {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false,
                selectedItem: null,
                selectedRowIndex: null
            });
        }
        else
        {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich das selektierte TabItem ändert.
     * @param index Der neue Index.
     */
    private onTabChanged = (index: number) =>
    {
        this.setState((state: IState) =>
        {
            state.selectedTabIndex = index;
            return state;
        });
    }

    /**
     * Render einen InfoText wenn die Liste leer ist.
     */
    private renderInfoText(): JSX.Element
    {
        return (
            <div className="settings-default_empty">
                <i className="settings-default_empty_icon mdi mdi-calendar"></i>
                <div className="settings-default_empty_header">Kalender</div>
                <div
                    style={{
                        width: 370
                    }}
                    className="settings-default_empty_text">
                    Erstellen Sie einen oder mehrere Kalender.
                </div>
                <Button
                    icon="plus"
                    text="Kalender"
                    type={ButtonType.primary}
                    size={ButtonSize.big}
                    onClick={this.onNewClick} />
            </div>
        );
    }

    /**
     * Rendert die Tabelle.
     */
    private renderTable(): JSX.Element
    {
        const { calendars, filteredCalendars } = this.state;
        const columns: ITableColumn[] = [
            {
                header: "Name",
                accessor: "name",
                width: 200
            },
            {
                header: "Gruppe",
                computedValue: (calendar: ICalendar) => calendar.group.group_name,
                width: 200
            },
            {
                header: "Friedhof",
                computedValue: (calendar: ICalendar) => calendar.cementery && calendar.cementery.name_long,
                width: 200
            },
            {
                header: "Farbe",
                cell: (calendar: ICalendar) =>
                {
                    let color: IColor = CALENDAR_COLORS.where(c => c.color === calendar.color).first();
                    return <ColorColumn color={color.color} width={100} />
                },
                width: 100
            }
        ];

        if (calendars != null && calendars.length > 0)
        {
            return (
                <Table
                    style={{
                        position: "relative"
                    }}
                    dataSource={filteredCalendars}
                    columns={columns}
                    keyAccessor="id"
                    onRowClick={this.onRowClick} />
            );
        }
        else
        {
            return this.renderInfoText();
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const { isLoading, selectedTabIndex, isDialogOpen, isDialogInEditMode, filterValue, calendars, selectedItem } = this.state;
        const isListEmpty: boolean = calendars == null || calendars.length === 0;
        const isItemSelected: boolean = selectedItem != null;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1}
                    infoText="Kalender werden geladen" />
            );
        }
        
        return (
            <div
                className="settings-default-container"
                style={{
                    padding: 0
                }}>
                {
                    !isListEmpty &&
                    <div className="settings-default_toolbar">
                        <ToolBar hasTopBorder={false}>
                            <ToolBarItem
                                icon="plus"
                                text="Neu"
                                onClick={this.onNewClick} />
                            <ToolBarSeparator />
                            <ToolBarItem
                                icon="pencil"
                                text="Bearbeiten"
                                onClick={this.onEditClick}
                                isDisabled={!isItemSelected} />
                        </ToolBar>
                    </div>
                }
                {
                    !isListEmpty &&
                    <div className="settings-default_filter">
                        <TextBox
                            placeholder="Filter Name..."
                            size={InputSize.normal}
                            value={filterValue}
                            onTextChange={this.onFilterTextChanged} />
                    </div>
                }
                <div className="settings-default">
                    {this.renderTable()}
                </div>
                {
                    isDialogOpen &&
                    <Dialog
                        calendarId={isDialogInEditMode && isItemSelected && selectedItem.id}
                        onClose={this.onDialogClosed} />
                }
            </div>
        );
    }
}